/** How a list of clauses is grouped, with one header style for every group (a section,
 * a gate, a package, a topic): title, kind, counts, chevron. A file's page reads its
 * clauses as the document's own outline, each gate and package in place where the
 * document states it; a specialist's card reads its goals first, then its topics.
 * A closed group mounts nothing, so its rows are never empty list items. */
import { type ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import type {
  GroupStatus,
  RegisterGoal,
  RegisterGroup,
  RegisterLine,
} from "../api";
import { hashId } from "../utils/useScrollToHash";
import {
  type CollapseSignal,
  CollapseContext,
  useCollapseSignal,
} from "./clauseCollapse";
import { SymbolCounts } from "./ClauseParts";
import styles from "./Clauses.module.css";
import {
  citedReference,
  countStatuses,
  type Grouping,
  goalAnchor,
  groupOf,
  KIND_OF,
  mergedTitle,
  quoteAddsInformation,
  sectionTitle,
  type StatusKind,
} from "./clauseStatus";
import { Chevron } from "./Chevron";

/** What every row of a group shares, said once on its header instead of on each row. */
export interface RowContext {
  topic: string | null;
  form: string | null;
  assessedBy: string | null;
}

const NONE: RowContext = { topic: null, form: null, assessedBy: null };

const sharedValue = <L,>(
  members: L[],
  of: (line: L) => string | null,
): string | null => {
  const first = members.length > 0 ? of(members[0]) : null;
  return first !== null && members.every((m) => of(m) === first) ? first : null;
};

const contextOf = <L extends RegisterLine>(
  members: L[],
  topic: string | null,
  assessedByOf: ((line: L) => string) | null,
): RowContext => ({
  topic,
  // Two rows or more: one row says it itself.
  form: members.length > 1 ? sharedValue(members, (l) => l.form) : null,
  assessedBy:
    assessedByOf && members.length > 1
      ? sharedValue(members, assessedByOf)
      : null,
});

const ContextLabels = ({ context }: { context: RowContext }) => (
  <>
    {context.form && (
      <span className={styles.badge}>
        Inside {context.form}, not yet executed
      </span>
    )}
    {context.assessedBy && (
      <span className={styles.muted}>{context.assessedBy}</span>
    )}
  </>
);

/** Expand all and Collapse all over the groups inside it. */
export const CollapseScope = ({
  extra,
  children,
}: {
  extra?: ReactNode;
  children: ReactNode;
}) => {
  const [signal, setSignal] = useState<CollapseSignal | null>(null);
  const press = (open: boolean) =>
    setSignal((s) => ({ open, n: (s?.n ?? 0) + 1 }));
  return (
    <>
      <div className={styles.collapseBar} data-review-chrome>
        <button
          type="button"
          className={styles.toggle}
          onClick={() => press(true)}
        >
          Expand all
        </button>
        <button
          type="button"
          className={styles.toggle}
          onClick={() => press(false)}
        >
          Collapse all
        </button>
        {extra}
      </div>
      <CollapseContext.Provider value={signal}>
        {children}
      </CollapseContext.Provider>
    </>
  );
};

/** A titled group that opens and closes: its body is mounted only while open, and it
 * opens itself when the address names something inside it. */
export const Band = ({
  id,
  title,
  tag,
  count,
  counts,
  aside,
  meta,
  defaultOpen = false,
  holds,
  children,
}: {
  id?: string;
  title: ReactNode;
  /** What kind of group it is, where that is not a plain section or band. */
  tag?: string;
  count: number;
  /** Its status counts, shown ahead of the title in place of the bare total. */
  counts?: ReactNode;
  aside?: ReactNode;
  /** A line under the title: what the group is, kept off the title's own line. */
  meta?: ReactNode;
  defaultOpen?: boolean;
  /** The ids inside it, so a link to one opens it. */
  holds?: Set<string>;
  children: ReactNode;
}) => {
  const target = hashId(useLocation().hash);
  const signal = useCollapseSignal();
  const [open, setOpen] = useState(
    (signal ? signal.open : defaultOpen) || (!!target && !!holds?.has(target)),
  );
  useEffect(() => {
    if (signal) setOpen(signal.open);
  }, [signal]);
  const holdsTarget = !!target && !!holds?.has(target);
  useEffect(() => {
    if (holdsTarget) setOpen(true);
  }, [target, holdsTarget]);
  return (
    <details
      id={id}
      className={styles.band}
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary className={styles.bandSummary}>
        {/* The caret inside the title, so a long title never leaves it alone on a line. */}
        <span className={styles.bandTitle}>
          <span className={styles.caret}>
            <Chevron tree />
          </span>{" "}
          {counts ?? <span className={styles.muted}>({count})</span>} {title}
        </span>
        {tag && <span className={styles.kindTag}>{tag}</span>}
        {aside && <span className={styles.bandAside}>{aside}</span>}
        {meta && <span className={styles.bandMeta}>{meta}</span>}
      </summary>
      {open && <div className={styles.bandBody}>{children}</div>}
    </details>
  );
};

/** Where a goal is stated: a link to its clause in the whole document, and its words
 * where they add a condition or a figure its title and due point leave out. */
const GoalSource = ({
  goal,
  sourceHref,
}: {
  goal: RegisterGoal;
  sourceHref: (lineId: string) => string;
}) =>
  goal.source ? (
    <span className={styles.goalSource}>
      <span className={styles.muted}>Stated in </span>
      <Link to={sourceHref(goal.source.line_id)}>
        {citedReference(goal.source.reference)}
      </Link>
      {quoteAddsInformation(goal) && (
        <span className={styles.muted}> &ldquo;{goal.quote}&rdquo;</span>
      )}
    </span>
  ) : null;

const plural = (n: number, noun: string): string =>
  `${n} ${noun}${n === 1 ? "" : "s"}`;

// What counts as done in a package of documents.
const DONE: StatusKind[] = ["met", "met_by_option", "not_applicable"];

const RISK_LABEL: Record<"at_risk" | "overdue", string> = {
  at_risk: "At risk",
  overdue: "Overdue",
};

const shortDay = (iso: string): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });

/** A goal on a project: the date it serves, when its clauses fall due, its risk. */
const GoalOnProjectLine = ({ goal }: { goal: RegisterGoal }) => {
  const here = goal.project;
  if (!here) return null;
  return (
    <span className={styles.goalProject}>
      {here.risk && (
        <span className={styles.badgeWarn}>{RISK_LABEL[here.risk]}</span>
      )}
      {here.milestone ? (
        <span className={styles.muted}>
          Serves {here.milestone.what}
          {here.milestone.date && ` ${shortDay(here.milestone.date)}`}
          {here.offset_days !== 0 &&
            here.due &&
            `; due ${shortDay(here.due)}, ${Math.abs(here.offset_days)} days ${
              here.offset_days > 0 ? "after" : "before"
            }`}
          {here.due_from &&
            `; due from ${shortDay(here.due_from)}, ${here.lead_days ?? 0} days' lead`}
        </span>
      ) : (
        <span className={styles.muted}>
          {here.milestone_none
            ? `No date yet: ${here.milestone_none}`
            : "Not yet tied to a project date"}
        </span>
      )}
      <span className={styles.muted}>
        {here.owner_label ?? "No owner"}:{" "}
        {here.status === "unassessed" ? "no verdict yet" : here.status.replace("_", " ")}
      </span>
    </span>
  );
};

/** A goal the document states, as a group: its members, its due point and where it is
 * stated; a package counts its documents. */
const GoalBand = <L extends RegisterLine>({
  goal,
  members,
  alsoHere,
  titleOfGoal,
  statusOf,
  renderLine,
  sourceHref,
  assessedByOf,
}: {
  goal: RegisterGoal;
  members: L[];
  /** Members shown under another goal they also serve. */
  alsoHere: { line: L; under: string }[];
  titleOfGoal: (key: string) => string;
  statusOf: ((line: L) => GroupStatus) | null;
  renderLine: (line: L, context: RowContext) => ReactNode;
  sourceHref: (lineId: string) => string;
  assessedByOf: ((line: L) => string) | null;
}) => {
  const statuses = statusOf ? members.map(statusOf) : null;
  const done = statuses?.filter((st) => DONE.includes(KIND_OF[st])).length;
  const context = contextOf(members, null, assessedByOf);
  return (
    <Band
      id={goalAnchor(goal.key)}
      defaultOpen
      holds={new Set(members.map((m) => m.line_id))}
      count={members.length}
      counts={
        statuses && statuses.length > 0 ? (
          <SymbolCounts counts={countStatuses(statuses)} />
        ) : undefined
      }
      title={goal.title}
      meta={
        <>
          {goal.due && <span className={styles.muted}>Due {goal.due}</span>}
          {goal.kind === "package" && (
            <span className={styles.badge}>
              {done === undefined
                ? plural(members.length, "document")
                : `${done} of ${plural(members.length, "document")} met`}
            </span>
          )}
          {goal.project ? (
            <GoalOnProjectLine goal={goal} />
          ) : (
            goal.owner_label && (
              <span className={styles.muted}>Owner: {goal.owner_label}</span>
            )
          )}
          <GoalSource goal={goal} sourceHref={sourceHref} />
          <ContextLabels context={context} />
        </>
      }
    >
      {members.length > 0 && (
        <ul className={styles.list}>
          {members.map((m) => renderLine(m, context))}
        </ul>
      )}
      {alsoHere.length > 0 && (
        <p className={styles.muted}>
          Also in this goal:{" "}
          {alsoHere
            .map(
              ({ line, under }) =>
                `${line.reference} (under ${titleOfGoal(under)})`,
            )
            .join(", ")}
        </p>
      )}
    </Band>
  );
};

/** Each goal with its members (a clause in two goals is shown under its first). */
const goalsWithMembers = <L extends RegisterLine>(
  goals: RegisterGoal[],
  lines: L[],
) => {
  const keys = new Set(goals.map((g) => g.key));
  const firstGoal = (l: L) => l.goal_keys.find((k) => keys.has(k));
  return {
    firstGoal,
    of: (goal: RegisterGoal) => ({
      members: lines.filter((l) => firstGoal(l) === goal.key),
      alsoHere: lines
        .filter(
          (l) => l.goal_keys.includes(goal.key) && firstGoal(l) !== goal.key,
        )
        .map((line) => ({ line, under: firstGoal(line) ?? "" })),
    }),
  };
};

/** A specialist's card: its goals first, in the document's order, then the rest by the
 * grouping's fallback (its topics). A header's counts are of its rows. */
export const ClauseGroups = <L extends RegisterLine>({
  lines,
  grouping,
  statusOf,
  renderLine,
  sourceHref,
}: {
  lines: L[];
  grouping: Grouping;
  sourceHref: (lineId: string) => string;
  statusOf: ((line: L) => GroupStatus) | null;
  renderLine: (line: L, context: RowContext) => ReactNode;
}) => {
  const { goals } = grouping;
  const goalTitle = new Map(goals.map((g) => [g.key, g.title]));
  const { firstGoal, of } = goalsWithMembers(goals, lines);
  const loose = lines.filter((l) => firstGoal(l) === undefined);
  const keyOf = (l: L) => groupOf(l, grouping);
  const keys = [...new Set([...grouping.order, ...loose.map(keyOf)])];
  return (
    <>
      {goals.map((goal) => {
        const { members, alsoHere } = of(goal);
        if (members.length + alsoHere.length === 0) return null;
        return (
          <GoalBand
            key={goal.key}
            goal={goal}
            members={members}
            alsoHere={alsoHere}
            titleOfGoal={(k) => goalTitle.get(k) ?? "another goal"}
            statusOf={statusOf}
            renderLine={renderLine}
            sourceHref={sourceHref}
            assessedByOf={null}
          />
        );
      })}
      {keys.map((key) => {
        const members = loose.filter((l) => keyOf(l) === key);
        if (members.length === 0) return null;
        const context = contextOf(members, key, null);
        return (
          <Band
            key={key}
            defaultOpen
            holds={new Set(members.map((m) => m.line_id))}
            count={members.length}
            counts={
              statusOf ? (
                <SymbolCounts counts={countStatuses(members.map(statusOf))} />
              ) : undefined
            }
            title={grouping.titleOf(key)}
            aside={<ContextLabels context={context} />}
          >
            <ul className={styles.list}>
              {members.map((m) => renderLine(m, context))}
            </ul>
          </Band>
        );
      })}
    </>
  );
};

type Item<L> =
  | { kind: "line"; position: number; line: L }
  | { kind: "goal"; position: number; goal: RegisterGoal }
  | { kind: "section"; position: number; group: RegisterGroup };

// Past this many rows a file's sections start closed; its gates and packages stay open.
const OPEN_SECTIONS_UP_TO = 150;

/** A file's page: the document's own outline as a nested tree, each gate and package in
 * place in the section stating it. */
export const ClauseTree = <L extends RegisterLine>({
  lines,
  allLines,
  goals,
  groups,
  renderLine,
  sourceHref,
  assessedByOf,
}: {
  /** The clauses shown. */
  lines: L[];
  /** Every clause of the version, to find where each goal is stated. */
  allLines: RegisterLine[];
  goals: RegisterGoal[];
  groups: RegisterGroup[];
  renderLine: (line: L, context: RowContext) => ReactNode;
  sourceHref: (lineId: string) => string;
  assessedByOf: (line: L) => string;
}) => {
  const goalTitle = new Map(goals.map((g) => [g.key, g.title]));
  const { firstGoal, of } = goalsWithMembers(goals, lines);
  const known = new Set(groups.map((g) => g.group_key));
  const source = new Map(allLines.map((l) => [l.line_id, l]));
  const ROOT = "";
  const home = (key: string) => (known.has(key) ? key : ROOT);
  const items = new Map<string, Item<L>[]>();
  const add = (parent: string, item: Item<L>) =>
    items.set(parent, [...(items.get(parent) ?? []), item]);
  for (const group of groups) {
    add(
      group.parent_key && known.has(group.parent_key) ? group.parent_key : ROOT,
      {
        kind: "section",
        position: group.position,
        group,
      },
    );
  }
  for (const line of lines) {
    if (firstGoal(line) === undefined)
      add(home(line.group_key), {
        kind: "line",
        position: line.position,
        line,
      });
  }
  const goalMembers = new Map(goals.map((g) => [g.key, of(g)]));
  for (const goal of goals) {
    const { members } = goalMembers.get(goal.key) ?? { members: [] };
    const stated = goal.source ? source.get(goal.source.line_id) : undefined;
    const at = stated ?? members[0];
    if (!at || members.length === 0) continue;
    add(home(at.group_key), { kind: "goal", position: at.position, goal });
  }
  // Rows and ids under each section, so empty sections are left out and a link opens the
  // sections around its target.
  const stats = new Map<string, { rows: number; ids: Set<string> }>();
  const statsOf = (key: string): { rows: number; ids: Set<string> } => {
    const cached = stats.get(key);
    if (cached) return cached;
    let rows = 0;
    const ids = new Set<string>();
    for (const item of items.get(key) ?? []) {
      if (item.kind === "line") {
        rows += 1;
        ids.add(item.line.line_id);
      } else if (item.kind === "goal") {
        const { members } = goalMembers.get(item.goal.key) ?? { members: [] };
        rows += members.length;
        ids.add(goalAnchor(item.goal.key));
        for (const m of members) ids.add(m.line_id);
      } else {
        const inner = statsOf(item.group.group_key);
        rows += inner.rows;
        for (const id of inner.ids) ids.add(id);
      }
    }
    const result = { rows, ids };
    stats.set(key, result);
    return result;
  };
  const sectionsOpen = lines.length <= OPEN_SECTIONS_UP_TO;
  const render = (key: string): ReactNode => {
    const sorted = [...(items.get(key) ?? [])].sort(
      (a, b) => a.position - b.position,
    );
    const out: ReactNode[] = [];
    let run: L[] = [];
    const flush = () => {
      if (run.length === 0) return;
      const rows = run;
      out.push(
        <ul
          key={`rows-${rows[0].line_id}-${rows[0].position}`}
          className={styles.list}
        >
          {rows.map((l) => renderLine(l, NONE))}
        </ul>,
      );
      run = [];
    };
    for (const item of sorted) {
      if (item.kind === "line") {
        run.push(item.line);
        continue;
      }
      flush();
      if (item.kind === "goal") {
        const { members, alsoHere } = goalMembers.get(item.goal.key) ?? {
          members: [],
          alsoHere: [],
        };
        out.push(
          <GoalBand
            key={item.goal.key}
            goal={item.goal}
            members={members}
            alsoHere={alsoHere}
            titleOfGoal={(k) => goalTitle.get(k) ?? "another goal"}
            statusOf={null}
            renderLine={renderLine}
            sourceHref={sourceHref}
            assessedByOf={assessedByOf}
          />,
        );
      } else {
        const { rows, ids } = statsOf(item.group.group_key);
        if (rows === 0) continue;
        // A section holding only a goal (a certificate's own section) is the goal: one header.
        const shown = (key: string) =>
          (items.get(key) ?? []).filter(
            (i) => i.kind !== "section" || statsOf(i.group.group_key).rows > 0,
          );
        const inner = shown(item.group.group_key);
        if (inner.length === 1 && inner[0].kind === "goal") {
          out.push(render(item.group.group_key));
          continue;
        }
        // A section holding only one section is that section: one header for both.
        let group = item.group;
        let title = sectionTitle(group.title);
        let condition = group.condition;
        for (
          let only = shown(group.group_key);
          only.length === 1 && only[0].kind === "section";
          only = shown(group.group_key)
        ) {
          group = only[0].group;
          title = mergedTitle(title, sectionTitle(group.title));
          condition =
            condition && group.condition && condition !== group.condition
              ? `${condition}; ${group.condition}`
              : (condition ?? group.condition);
        }
        out.push(
          <Band
            key={item.group.group_key}
            defaultOpen={sectionsOpen}
            holds={ids}
            count={rows}
            title={title}
            aside={
              condition ? (
                <span className={styles.applies}>
                  Applies only if {condition}
                </span>
              ) : undefined
            }
          >
            {render(group.group_key)}
          </Band>,
        );
      }
    }
    flush();
    return out;
  };
  return <>{render(ROOT)}</>;
};

/** A list's checklist in two parts: its milestones (gates and packages) in delivery order,
 * each with its clauses from whichever file states them, then every other clause by file
 * and section, a list of items in the section stating it. */
export const MilestonesThenDocument = <L extends RegisterLine>({
  lines,
  allLines,
  goals,
  groups,
  renderLine,
  sourceHref,
  assessedByOf,
}: {
  lines: L[];
  allLines: RegisterLine[];
  goals: RegisterGoal[];
  groups: RegisterGroup[];
  renderLine: (line: L, context: RowContext) => ReactNode;
  sourceHref: (lineId: string) => string;
  assessedByOf: (line: L) => string;
}) => {
  const goalTitle = new Map(goals.map((g) => [g.key, g.title]));
  const gates = goals.filter((g) => g.kind === "gate" || g.kind === "package");
  const { firstGoal, of } = goalsWithMembers(gates, lines);
  // A package is assessed as its list row: the clause stating it, its documents nested.
  const packageRow = new Map(
    gates.flatMap((g) => {
      const row = g.kind === "package" ? lines.find((l) => l.line_id === g.source?.line_id) : undefined;
      return row ? [[g.key, row] as const] : [];
    }),
  );
  const placedIn = new Set([...packageRow.values()].map((l) => l.line_id));
  const milestones = gates
    .map((goal) => {
      const found = of(goal);
      const row = packageRow.get(goal.key);
      return { goal, ...found, members: row ? [row, ...found.members] : found.members };
    })
    .filter((m) => m.members.length + m.alsoHere.length > 0);
  const rest = lines.filter((l) => firstGoal(l) === undefined && !placedIn.has(l.line_id));
  return (
    <>
      {milestones.length > 0 && (
        <section aria-labelledby="milestones-heading">
          <h3 id="milestones-heading" className={styles.partHead}>
            Milestones
          </h3>
          {milestones.map(({ goal, members, alsoHere }) => (
            <GoalBand
              key={goal.key}
              goal={goal}
              members={members}
              alsoHere={alsoHere}
              titleOfGoal={(k) => goalTitle.get(k) ?? "another goal"}
              statusOf={null}
              renderLine={renderLine}
              sourceHref={sourceHref}
              assessedByOf={assessedByOf}
            />
          ))}
        </section>
      )}
      {rest.length > 0 && (
        <section aria-labelledby="by-document-heading">
          <h3 id="by-document-heading" className={styles.partHead}>
            {milestones.length > 0 ? "Other clauses, by document" : "By document"}
          </h3>
          <ClauseTree
            lines={rest}
            allLines={allLines}
            goals={goals.filter((g) => !gates.includes(g))}
            groups={groups}
            renderLine={renderLine}
            sourceHref={sourceHref}
            assessedByOf={assessedByOf}
          />
        </section>
      )}
    </>
  );
};
